import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import useTheme from '../../hooks/useTheme';
import { StyledRatingStars, StyledRatingStarsContainer } from './styled';
import Typography from '../Typography';
import { makeStyles } from '../../utils/styles';

const useStyles = makeStyles(css => ({
    font: css`
        font-size: 18px;
        line-height: 18px;
        font-weight: 700;
        margin-left: 20px;
    `,
    fontSmall: css`
        font-size: 14px;
        line-height: 14px;
        font-weight: 700;
        margin-left: 10px;
   `,
}));

const RatingStars = ({ rate, interactive, onClickStar, small }) => {
    const styles = useStyles();
    const theme = useTheme();
    const starsRef = useRef(null);

    const parsedRate = useMemo(() => rate === null || rate === undefined ? 0 : parseFloat(rate), [rate]);

    const calcWith = (index, parsedRate) => {
        const width = index + 1 < parsedRate ? 20 : (parsedRate - index) * 100 * 0.2;
        if (width >= 1 && width <= 5) return width;
        if (width > 5) return 20;
        if (width < 1) return 0;
    };

    useEffect(() => {
        handleMouseOver(parsedRate - 1)();
        // eslint-disable-next-line
    }, [parsedRate])

    const handleMouseOver = id => () => {
        if (!interactive) return;
        const stars = starsRef.current.childNodes;
        return stars?.forEach((star, index) => {
            if (index <= id) {
                star.classList.add('active')
            } else {
                star.classList.remove('active')
            }
        });
    }

    const handleMouseOut = () => {
        if (!interactive) return;
        const stars = starsRef.current.childNodes;
        return stars?.forEach((star, index) => {
            if (index <= parsedRate - 1) {
                star.classList.add('active')
            } else {
                star.classList.remove('active')
            }
        });
    }

    const handleClickStar = index => () => interactive && onClickStar(index + 1);

    return (
        <StyledRatingStarsContainer>
            <StyledRatingStars
                ref={starsRef}
                onMouseOut={handleMouseOut}
                interactive={interactive}
            >
                {
                    Array(5).fill(0).map((star, index) => (
                        <svg
                            className={`star`}
                            key={index}
                            width={`${small ? "12px" : "22px"}`}
                            height={`${small ? "12px" : "22px"}`}
                            viewBox="0 0 20 20"
                            onMouseOver={handleMouseOver(index)}
                            onClick={handleClickStar(index)}
                        >
                            <defs>
                                {
                                    !interactive && (
                                        <clipPath id={`starClipPath-${index}-${rate}`}>
                                            <rect width={calcWith(index, parsedRate)} height="20" />
                                        </clipPath>
                                    )
                                }
                                <path id={`starPath-${index}-${rate}`} d="M4.166 12.458a1 1 0 01.271.863l-.946 5.55a.976.976 0 00.357.929.879.879 0 00.953.085l4.75-2.663a.89.89 0 01.873 0l4.75 2.663a.879.879 0 00.971-.068.976.976 0 00.36-.946l-.939-5.55a1 1 0 01.271-.863l3.886-3.905a.99.99 0 00.233-.989.929.929 0 00-.748-.651l-5.331-.767a.929.929 0 01-.7-.53L10.824.536a.9.9 0 00-1.646 0L6.829 5.615a.928.928 0 01-.72.53l-5.318.768a.929.929 0 00-.745.653.99.99 0 00.235.987z" />
                            </defs>
                            <g>
                                <use xlinkHref={`#starPath-${index}-${rate}`} fill={theme.colors.mediumGrey} />
                                {!interactive && <use clipPath={`url(#starClipPath-${index}-${rate})`} xlinkHref={`#starPath-${index}-${rate}`} fill={theme.colors.orange} />}
                            </g>
                        </svg>
                    ))
                }
            </StyledRatingStars>
            <Typography as="span" customStyle={small ? styles.fontSmall : styles.font}>
                {!interactive ? Number.isInteger(parsedRate) ? parsedRate : parsedRate.toFixed(1) : parsedRate}
            </Typography>
        </StyledRatingStarsContainer>
    );
};

RatingStars.propTypes = {
    rate: PropTypes.number,
    interactive: PropTypes.bool,
    onClickStar: PropTypes.func
};

RatingStars.defaultProps = {
    interactive: false,
    onClickStar: undefined
};

export default React.memo(RatingStars);